/**
 * Human-readable incident titles.
 *
 * OWNER, 2026-09-03: "the incident title in entities is showing a tech title.
 * No capitalization and often has underscores. Needs to be human
 * readable/displayable."
 *
 * The extractor emits `incidentType` as a short machine-ish label — the field's
 * own description in `api-ai-engine/app/core/models/entities.py` is "Type:
 * theft, assault, disturbance…" — so values arrive as `vandalism`,
 * `property_damage`, `break-in`. That is the right shape to STORE and to match
 * on; it is the wrong shape to show an officer. This converts at the display
 * edge only: nothing about the stored value changes, so filters, matching and
 * the extractor's vocabulary are untouched.
 *
 * Rules, in order:
 *  - underscores become spaces; hyphens do NOT ("break-in" stays "Break-in");
 *  - a word already carrying an upper-case letter is left alone, so acronyms
 *    and codes survive ("DUI", "B&E", "MVA", "911");
 *  - the first word is always capitalised; later connectors stay lower-case,
 *    so "theft of vehicle" reads "Theft of Vehicle" rather than "Theft Of
 *    Vehicle";
 *  - a value that is already human ("Vehicle Damage") comes back unchanged.
 */

/** Lower-case in the middle of a title, capitalised at the start. */
const CONNECTORS: ReadonlySet<string> = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'nor', 'of',
  'on', 'or', 'the', 'to', 'via', 'with',
]);

function capitaliseWord(word: string, isFirst: boolean): string {
  if (word.length === 0) return word;
  // Anything the source already capitalised is a name, an acronym or a code.
  if (/[A-Z]/.test(word)) return word;
  if (!isFirst && CONNECTORS.has(word)) return word;
  return word[0].toUpperCase() + word.slice(1);
}

/**
 * The officer-facing form of an incident type, or '' when there is nothing to
 * show. Never throws, and never invents a value for an empty input — a caller
 * that wants a placeholder supplies its own.
 */
export function humanizeIncidentType(value: string | null | undefined): string {
  if (typeof value !== 'string') return '';
  const spaced = value.replace(/_+/g, ' ').trim().replace(/\s+/g, ' ');
  if (spaced.length === 0) return '';
  return spaced
    .split(' ')
    .map((word, index) => capitaliseWord(word, index === 0))
    .join(' ');
}
